import { ConverterRegistry } from '../core/converter';
import type { ConverterCapabilities } from '../core/types';
import { registerAiEngines, type AiOcrDeps } from './ai/registry';
import { registerDocumentEngines, type DocumentEngineDeps } from './document/registry';
import { registerImageEngines } from './image/registry';
import type { ImageConverterDeps } from './image/browser-image-converter';

/** 引擎依赖注入：测试用假实现替换重量级解析库，生产走默认实现。 */
export interface EngineDeps {
  image?: ImageConverterDeps;
  document?: DocumentEngineDeps;
  ai?: AiOcrDeps;
}

/**
 * 引擎注册表是格式能力的唯一来源：UI 只能通过它查询分类、源格式与目标格式，
 * 不得在组件里硬编码格式清单。
 *
 * 注册顺序（= 默认选择顺序）：图片 → 文档本地 → AI 通道。
 * 同一目标格式有多条通道时由 UI 让用户显式选择（`ConverterRegistry.candidatesFor`）。
 */
export function createRegistry(deps: EngineDeps = {}): ConverterRegistry {
  const registry = new ConverterRegistry();
  registerImageEngines(registry, deps.image);
  registerDocumentEngines(registry, deps.document);
  registerAiEngines(registry, deps.ai);
  return registry;
}

/**
 * 异步能力探测：把「运行时才知道」的能力（如 AVIF WASM 编码器是否可用）
 * 反映到能力列表，UI 据此禁用目标格式，禁止静默失败。
 */
export async function probeCapabilities(registry: ConverterRegistry): Promise<ConverterCapabilities[]> {
  return Promise.all(
    registry.list().map(async (capability) => {
      const converter = registry.byId(capability.id);
      if (!converter?.probe) return capability;
      try {
        return await converter.probe();
      } catch {
        return { ...capability, available: false, unavailableReason: '能力探测失败' };
      }
    }),
  );
}
